import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { usePreviewBase } from './usePreviewBase';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { Environment } from '~/engine/data/Environment';
import { computeUnionTexture } from '~/engine/util/algorithms/sdf/unionPipeline';
import { GlOperationGuard } from '~/engine/util/algorithms/sdf/utils';
import { getErrorMessage } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';
import { waitForUI } from '~/utils/uiUtils';
import { dicomColorToVec3 } from '~/utils/color';

/**
 * Geometry-determining parameters of one bulk-union preview — exactly the
 * inputs {@link computeUnionTexture} consumes, plus the content version of
 * every texture involved. Name, type and colour are deliberately absent:
 * they do not change the geometry, so editing them must never mark a held
 * preview recompute (colour follows the preview by a render-only repaint).
 */
export interface BulkUnionPreviewParams {
    /**
     * Sources in panel order, each with the content version of its engine
     * texture sampled on render — an edited source marks a held preview
     * stale (best-effort: the version is sampled on render, so an edit
     * between renders shows up one render later; the same contract as the
     * threshold segmentation's keep-inside token).
     */
    sources: readonly { roiNumber: number; sdfVersion: number }[];
    /** Per-slice (2D) hole fill, run before the margin — the boolean IS
     *  the geometry-determining input, so it participates in the key
     *  directly (a filled preview must never stand in for an unfilled
     *  result, nor the reverse). */
    fillHoles2d: boolean;
    /** Domain unit; the dialog converts from the cm it displays. */
    marginMm: number;
    /** cm³ — the legacy unit of the remove-small-parts stage; undefined
     *  when the stage is off. */
    minVolumeCm3?: number;
    /**
     * EXTERNAL to intersect with, or null when the keep-inside toggle is
     * off OR no usable EXTERNAL exists. The two nulls share one key token
     * on purpose — both run the identical pipeline (no clip stage) — while
     * the TOGGLE still participates in the key: flipping it on resolves a
     * roiNumber@version token that no null state can produce, so a preview
     * taken without the clip is never presented as a clipped result, and
     * vice versa. `contractionMm` is the skin margin — how far inside the
     * EXTERNAL surface the clip boundary lies (0 = at the surface) — and
     * participates in the key like every other geometry-determining input.
     */
    keepInside: {
        roiNumber: number;
        sdfVersion: number;
        contractionMm: number;
    } | null;
}

/** Staleness key of a union preview — see {@link BulkUnionPreviewParams}
 *  for what participates and why. */
export function bulkUnionPreviewKey(params: BulkUnionPreviewParams): string {
    const sources = params.sources
        .map((s) => `${s.roiNumber}@${s.sdfVersion}`)
        .join('+');
    const fill = params.fillHoles2d ? 'on' : 'off';
    const min = params.minVolumeCm3 ?? 'off';
    const inExt = params.keepInside
        ? `${params.keepInside.roiNumber}@${params.keepInside.sdfVersion}` +
          `~${params.keepInside.contractionMm}`
        : 'off';
    // Tokens in pipeline order (fill before margin), matching the stage
    // order the key describes.
    return (
        `bulk-union:${sources}|fill:${fill}|margin:${params.marginMm}` +
        `|min:${min}|inExt:${inExt}`
    );
}

interface UseBulkUnionPreviewOptions {
    /**
     * Environment the sources and the EXTERNAL are read from and the
     * preview is drawn on. It must be the Environment Apply writes to, or
     * the displayed union is not the one committed.
     */
    environment: Environment | null;
}

/**
 * Preview lifecycle for the bulk-union operation (SR-WOR-H-26-2), on the
 * shared {@link usePreviewBase} mechanics the threshold segmentation uses.
 * The preview runs the SAME pipeline as apply ({@link computeUnionTexture})
 * and publishes the result as a render-only overlay: it never creates a
 * structure, never writes to one, and above all never deletes a source —
 * only Apply does, and only through its own opt-in. Previews run only on
 * the panel's own initiative — the caller re-runs it whenever
 * {@link bulkUnionPreviewKey} changes, so the displayed overlay is always
 * the current parameters' result rather than one the user must refresh.
 *
 * An empty result (contraction margin, disjoint EXTERNAL) is never
 * published: a blank overlay would be indistinguishable from "preview did
 * nothing", and refusing to hold it also guarantees {@link consumePreview}
 * can never hand an empty texture to apply.
 */
export function useBulkUnionPreview({
    environment,
}: UseBulkUnionPreviewOptions) {
    const intl = useIntl();
    const {
        isPreviewing,
        beginPreviewRun,
        endPreviewRun,
        beginOperation,
        setResult,
        consumeResult,
        recolorResult,
        abortInFlight,
        cleanup,
        heldKey,
    } = usePreviewBase({ environment });

    const handlePreview = useCallback(
        async (
            params: BulkUnionPreviewParams,
            color: DicomColor,
            options?: {
                /**
                 * Suppress the "another operation is running" warning.
                 * Set by an AUTOMATIC caller, which re-offers the run
                 * itself and must not narrate a transient contention the
                 * user did not ask for.
                 */
                quiet?: boolean;
            }
        ): Promise<boolean> => {
            const structureSet = environment?.structureSet;
            if (!environment?.image || !structureSet) {
                return true;
            }
            if (GlOperationGuard.isActive) {
                // Same synchronous refusal as the apply path: the SDF
                // operations cannot share the GL guard with another
                // running operation. Reported as NOT attempted, so an
                // automatic caller re-offers it once the GPU frees up.
                if (options?.quiet !== true) {
                    enqueueSnackbar(
                        intl.formatMessage({ id: 'structure.bulk.union.busy' }),
                        { variant: 'warning' }
                    );
                }
                return false;
            }

            // Resolve every input BEFORE any compute starts, failing
            // rather than skipping (the useBulkUnion contract): a preview
            // of fewer sources, or without a requested clip, would show a
            // result the eventual Apply does not produce.
            const sources: ContourTexture[] = [];
            for (const source of params.sources) {
                const texture = structureSet.getRoi(source.roiNumber)?.texture;
                if (!texture || texture.isEmpty) {
                    cleanup();
                    enqueueSnackbar(
                        intl.formatMessage({
                            id: 'structure.bulk.union.sourceGone',
                        }),
                        { variant: 'error' }
                    );
                    return true;
                }
                sources.push(texture);
            }
            let keepInsideTexture: ContourTexture | undefined;
            if (params.keepInside !== null) {
                keepInsideTexture = structureSet.getRoi(
                    params.keepInside.roiNumber
                )?.texture;
                if (!keepInsideTexture) {
                    cleanup();
                    enqueueSnackbar(
                        intl.formatMessage({
                            id: 'structure.bulk.union.externalGone',
                        }),
                        { variant: 'error' }
                    );
                    return true;
                }
            }

            const signal = beginOperation();
            const runId = beginPreviewRun();
            // Same main-thread yield the other SDF previews use so the busy
            // state paints before the compute starts.
            await waitForUI();
            try {
                const texture = await computeUnionTexture(
                    sources,
                    {
                        fillHoles2d: params.fillHoles2d,
                        marginMm: params.marginMm,
                        minVolumeCm3: params.minVolumeCm3,
                        keepInsideTexture,
                        keepInsideContractionMm:
                            params.keepInside?.contractionMm,
                    },
                    signal
                );
                if (signal.aborted) {
                    texture.delete();
                    return true;
                }
                if (texture.isEmpty) {
                    // Refuse the blank overlay and drop any held preview
                    // so a stale outline cannot masquerade as this result.
                    texture.delete();
                    cleanup();
                    enqueueSnackbar(
                        intl.formatMessage({
                            id: 'structure.bulk.union.previewEmpty',
                        }),
                        { variant: 'warning' }
                    );
                    return true;
                }

                // The wrapper owns the FRESHLY computed texture — never a
                // source's or the EXTERNAL's own ContourTexture, whose
                // lifetimes belong to their ROIs (the preview slot deletes
                // what it holds).
                const previewObj = new CustomTargetObject(
                    environment.image,
                    texture,
                    dicomColorToVec3(color)
                );
                previewObj.renderOptions = { fillAlpha: 0.18 };
                // The union preview draws exactly OVER its source
                // structures — the same anatomy, possibly in the very
                // colour of the first source it was seeded from — so
                // without the recede it would be indistinguishable from
                // the sources' own contours. That is the margin / ring /
                // boolean / Body case, not the High-density one: recede
                // (dimsOtherStructures stays at its default true).
                setResult(previewObj, bulkUnionPreviewKey(params));
            } catch (e) {
                if ((e as DOMException)?.name === 'AbortError') {
                    // Superseded by a newer preview — not an error.
                    return true;
                }
                console.error('Bulk union preview failed:', e);
                cleanup();
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'structure.bulk.union.previewFailed' },
                        { error: getErrorMessage(e) }
                    ),
                    { variant: 'error' }
                );
            } finally {
                endPreviewRun(runId);
            }
            return true;
        },
        [
            environment,
            beginOperation,
            setResult,
            cleanup,
            beginPreviewRun,
            endPreviewRun,
            intl,
        ]
    );

    /** Hand off the held preview's texture when it matches EXACTLY these
     *  parameters; undefined otherwise (apply then recomputes). */
    const consumePreview = useCallback(
        (params: BulkUnionPreviewParams): ContourTexture | undefined =>
            consumeResult(bulkUnionPreviewKey(params)),
        [consumeResult]
    );

    /** Repaint the held preview in a new color — no recompute, no
     *  staleness: the underlying texture is untouched. No-op while no
     *  preview is held. */
    const recolorPreview = useCallback(
        (color: DicomColor) => {
            recolorResult(dicomColorToVec3(color));
        },
        [recolorResult]
    );

    return useMemo(
        () => ({
            isPreviewing,
            handlePreview,
            consumePreview,
            recolorPreview,
            abortInFlight,
            cleanup,
            heldKey,
        }),
        [
            isPreviewing,
            handlePreview,
            consumePreview,
            recolorPreview,
            abortInFlight,
            cleanup,
            heldKey,
        ]
    );
}
